import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2, ExternalLink, Gift, CheckCircle2, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { id: "", label: "All" },
  { id: "active", label: "Paid · Active" },
  { id: "pending", label: "Pending Payment" },
  { id: "past_due", label: "Past Due" },
  { id: "canceled", label: "Canceled" },
];

function statusPill(a) {
  const s = a.subscription_status;
  if (a.free_trial_active) {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border bg-sg-gold/15 text-sg-gold-deep border-sg-gold/40">
        Free Trial
      </span>
    );
  }
  const map = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    past_due: "bg-orange-50 text-orange-800 border-orange-200",
    canceled: "bg-gray-50 text-gray-700 border-gray-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${map[s] || "bg-gray-50 border-gray-200"}`}>{s || "—"}</span>;
}

export default function AdminAgents() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [trialStats, setTrialStats] = useState({ used: 0, total: 100, remaining: 100 });
  const [grantingId, setGrantingId] = useState(null);

  const loadTrialStats = async () => {
    try {
      const { data } = await api.get("/admin/free-trial-stats");
      setTrialStats(data);
    } catch (_e) { /* leave defaults */ }
  };

  useEffect(() => {
    loadTrialStats();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (status) params.status = status;
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/admin/agents", { params });
        if (cancelled) return;
        setRows(data.agents || []);
        setTotal(data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [status, q]);

  const handleGrantTrial = async (agent) => {
    if (grantingId) return;
    setGrantingId(agent.user_id);
    try {
      const { data } = await api.post(`/admin/users/${agent.user_id}/grant-free-trial`);
      toast.success(`Free trial granted — expires ${new Date(data.trial_expires_at).toLocaleDateString("en-IN")} · ${data.days_remaining} days left`);
      // Refresh both the row and the counter
      setRows((prev) => prev.map((a) => a.user_id === agent.user_id ? {
        ...a,
        subscription_status: "active",
        subscription_renew_at: data.trial_expires_at,
        free_trial_active: true,
        free_trial_granted_at: new Date().toISOString(),
        free_trial_expires_at: data.trial_expires_at,
      } : a));
      setTrialStats((s) => ({ ...s, used: data.slots_used, remaining: Math.max(0, data.slots_total - data.slots_used) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grant free trial.");
    } finally {
      setGrantingId(null);
    }
  };

  const slotsExhausted = trialStats.remaining <= 0;

  const handleRenameName = async (agent) => {
    const input = window.prompt(
      `Rename agent display name.\n\nCurrent: ${agent.name}\nThis updates the "Hello, X" greeting and the microsite header.`,
      agent.name || "",
    );
    if (input == null) return;
    const next = input.trim();
    if (!next || next === agent.name) return;
    try {
      const { data } = await api.post(`/admin/users/${agent.user_id}/rename`, { name: next });
      toast.success(`Renamed to "${data.name}".`);
      setRows((prev) => prev.map((a) => a.user_id === agent.user_id ? { ...a, name: data.name } : a));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename agent.");
    }
  };

  const handleRenameSlug = async (agent) => {
    const current = agent.slug || "";
    const input = window.prompt(
      `Rename portal slug for ${agent.name}.\n\nCurrent: /agent/${current || "(none)"}\nThe old slug will still redirect to the new one.`,
      current,
    );
    if (input == null) return; // cancel
    const next = input.trim().toLowerCase();
    if (!next || next === current) return;
    try {
      const { data } = await api.post(`/admin/users/${agent.user_id}/rename-slug`, { new_slug: next });
      toast.success(`Portal slug updated → /agent/${data.slug}`);
      setRows((prev) => prev.map((a) => a.user_id === agent.user_id ? { ...a, slug: data.slug } : a));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename slug.");
    }
  };

  return (
    <DashboardShell>
      <div data-testid="admin-agents">
        <div className="overline text-sg-gold-deep">REGISTERED AGENTS</div>
        <div className="flex items-start justify-between flex-wrap gap-4 mt-1">
          <div>
            <h1 className="font-display text-4xl text-sg-navy">All <span className="font-serif-italic text-sg-gold-deep">agents</span></h1>
            <p className="text-sg-mute mt-2">{total} total · live data</p>
          </div>
          <div
            className={`rounded-2xl border px-5 py-3 ${slotsExhausted ? "border-red-200 bg-red-50" : "border-sg-gold/40 bg-sg-gold/10"}`}
            data-testid="free-trial-counter"
          >
            <div className="text-[10px] uppercase tracking-widest text-sg-gold-deep font-semibold flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5" /> Free Trial Slots
            </div>
            <div className="mt-1 font-mono text-2xl text-sg-navy">
              <strong data-testid="free-trial-used">{trialStats.used}</strong>
              <span className="text-sg-mute mx-1">/</span>
              {trialStats.total}
            </div>
            <div className={`mt-0.5 text-[11px] ${slotsExhausted ? "text-red-700 font-semibold" : "text-sg-mute"}`}>
              {slotsExhausted ? "Free trial slots exhausted" : `${trialStats.remaining} remaining · ${trialStats.trial_days || 15} days each`}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-sg-mute" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / slug" className="pl-9 h-11 w-72 bg-white" data-testid="admin-agents-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id || "all"}
                onClick={() => setStatus(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs border ${status === f.id ? "bg-sg-navy text-white border-sg-navy" : "bg-white text-sg-navy border-sg-line"}`}
                data-testid={`admin-filter-${f.id || "all"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-white border border-sg-line rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sg-cream-soft text-sg-mute text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Agent</th>
                  <th className="text-left px-4 py-3">Slug</th>
                  <th className="text-left px-4 py-3">Locality</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Renews / Expires</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sg-mute"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sg-mute">No agents match.</td></tr>
                ) : (
                  rows.map((a) => {
                    const hasTrial = !!a.free_trial_granted_at;
                    const canGrant = !hasTrial && !slotsExhausted
                      && a.subscription_status !== "active";
                    return (
                      <tr key={a.user_id} className="border-t border-sg-line" data-testid={`admin-agent-row-${a.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="font-medium text-sg-navy">{a.name}</div>
                              <div className="text-xs text-sg-mute font-mono">{a.email}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRenameName(a)}
                              className="text-sg-mute hover:text-sg-gold-deep transition-colors"
                              title="Fix a typo in the agent's display name"
                              data-testid={`admin-agent-rename-name-${a.user_id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          <div className="inline-flex items-center gap-2">
                            <span>{a.slug || "—"}</span>
                            {a.slug && (
                              <button
                                type="button"
                                onClick={() => handleRenameSlug(a)}
                                className="text-sg-mute hover:text-sg-gold-deep transition-colors"
                                title="Rename portal slug"
                                data-testid={`admin-agent-rename-slug-${a.user_id}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">{a.primary_locality || "—"}</td>
                        <td className="px-4 py-3">{statusPill(a)}</td>
                        <td className="px-4 py-3 text-xs font-mono">{a.subscription_renew_at ? new Date(a.subscription_renew_at).toLocaleDateString("en-IN") : "—"}</td>
                        <td className="px-4 py-3 text-xs font-mono">{a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN") : "—"}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-3">
                            {hasTrial ? (
                              <span
                                className="inline-flex items-center gap-1 text-xs text-sg-gold-deep"
                                data-testid={`admin-agent-trialed-${a.user_id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Trial granted
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleGrantTrial(a)}
                                disabled={!canGrant || grantingId === a.user_id}
                                title={
                                  slotsExhausted ? "Free trial slots exhausted"
                                  : a.subscription_status === "active" ? "Agent is already on a paid subscription"
                                  : "Grant a 15-day free trial from their registration date"
                                }
                                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                  canGrant
                                    ? "bg-sg-gold/10 hover:bg-sg-gold/20 text-sg-gold-deep border-sg-gold/40"
                                    : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                                }`}
                                data-testid={`admin-agent-grant-trial-${a.user_id}`}
                              >
                                {grantingId === a.user_id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Gift className="w-3.5 h-3.5" />}
                                {grantingId === a.user_id ? "Granting…" : "Grant Free Trial"}
                              </button>
                            )}
                            {a.slug && a.onboarded && (
                              <Link to={`/agent/${a.slug}`} target="_blank" className="text-sg-gold-deep text-xs inline-flex items-center gap-1 border-b border-sg-gold" data-testid={`admin-agent-microsite-${a.user_id}`}>
                                Portal <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
